import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { motion } from "framer-motion";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { AppDispatch, RootState } from "../../stores";
import {
  getBillingHistoryAction,
  downloadInvoiceAction,
} from "../../features/subscription-module/subscriptionAction";
import { toast } from "react-hot-toast";
import { clearSubscriptionMessages } from "../../features/subscription-module/susbscriptionSlice";

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: theme.spacing(2),
  width: "100%",
  maxWidth: "auto",
  minHeight: 380,
  margin: theme.spacing(2, "auto"),
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    maxWidth: "100%",
    margin: theme.spacing(1, 0),
    minHeight: 280,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "11px",
  fontWeight: 500,
  padding: theme.spacing(0.3),
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    padding: theme.spacing(0.2),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const NoHistoryMessage = styled(Typography)(({ theme }) => ({
  color: "#757575",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "14px",
  fontWeight: 500,
  textAlign: "center",
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "360px",
  width: "100%",
  height: "100%",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const DownloadButton = styled(IconButton)(({ theme }) => ({
  color: "#4b5b5d",
  padding: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: "rgba(75, 91, 93, 0.08)",
  },
  "&:disabled": {
    color: "#ccc",
  },
}));

const BillingHistoryCard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    billingHistory,
    billingHistoryLoading,
    billingHistoryError,
    invoiceLoading,
    invoiceError,
  } = useSelector((state: RootState) => state.subscription);

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | null
  >(null);

  // Helper function to truncate description
  const truncateDescription = (description: string, maxLength: number = 15) => {
    if (description.length > maxLength) {
      return `${description.slice(0, maxLength)}...`;
    }
    return description;
  };

  useEffect(() => {
    dispatch(getBillingHistoryAction());
    return () => {
      dispatch(clearSubscriptionMessages());
    };
  }, [dispatch]);

  const handleDownloadInvoice = async (invoiceId: string, status: string) => {
    if (status.toLowerCase() === "pending") {
      toast.error(
        "The invoice is not available as this is a scheduled plan that has not yet been activated."
      );
      return;
    }
    if (status.toLowerCase() === "canceled") {
      toast.error("The invoice is not available as this plan was canceled.");
      return;
    }

    if (!billingHistoryLoading && !invoiceLoading) {
      setDownloadingInvoiceId(invoiceId);
      try {
        const response = await dispatch(
          downloadInvoiceAction(invoiceId)
        ).unwrap();
        if (response.success && response.data?.pdfUrl) {
          toast.success("Invoice download started");
          const link = document.createElement("a");
          link.href = response.data.pdfUrl;
          link.download = `invoice-${invoiceId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          toast.error(response.message || "Failed to fetch invoice PDF");
        }
      } catch (error) {
        toast.error(invoiceError || "Error downloading invoice");
      } finally {
        setDownloadingInvoiceId(null);
      }
    }
  };

  const isNoBillingHistory =
    billingHistoryError &&
    (billingHistoryError.includes("Subscription not found") ||
      billingHistoryError.includes("No billing history found"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <Card>
        <Title>Billing History</Title>
        {billingHistoryLoading ? (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading billing history...</LoaderText>
          </LoaderContainer>
        ) : billingHistoryError && !billingHistoryLoading ? (
          isNoBillingHistory ? (
            <NoHistoryMessage>No billing history available.</NoHistoryMessage>
          ) : (
            <ErrorMessage>{billingHistoryError}</ErrorMessage>
          )
        ) : (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Table>
              <TableHead sx={{ background: "#f5f0e7" }}>
                <TableRow>
                  <StyledTableCell>Billing Date</StyledTableCell>
                  <StyledTableCell>Total Price</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Description</StyledTableCell>
                  <StyledTableCell>Invoice</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory && billingHistory.length > 0 ? (
                  billingHistory.map((item) => (
                    <StyledTableRow key={item.billingHistoryId}>
                      <StyledTableCell>
                        {item.paidAt
                          ? format(new Date(item.paidAt), "MM-dd-yyyy")
                          : format(new Date(item.createdAt), "MM-dd-yyyy")}
                      </StyledTableCell>
                      <StyledTableCell>${item.amount}</StyledTableCell>
                      <StyledTableCell>
                        {item.status.toUpperCase()}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title={item.description} arrow>
                          <span>{truncateDescription(item.description)}</span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title="Download Invoice">
                          <DownloadButton
                            color="inherit"
                            size="small"
                            onClick={() =>
                              handleDownloadInvoice(
                                item.stripeInvoiceId,
                                item.status
                              )
                            }
                            disabled={
                              billingHistoryLoading ||
                              invoiceLoading ||
                              downloadingInvoiceId === item.billingHistoryId
                            }
                          >
                            {downloadingInvoiceId === item.stripeInvoiceId ? (
                              <CircularProgress
                                size={16}
                                thickness={4}
                                sx={{
                                  color: "#4b5b5d",
                                }}
                              />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </DownloadButton>
                        </Tooltip>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={5} align="center">
                      No billing history available
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </motion.div>
  );
};

export default BillingHistoryCard;
